import React from 'react';

import Modal from 'antd/es/modal/Modal';
import {
	Field,
	Form,
	Formik,
	FormikProps,
} from 'formik';
import * as Yup from 'yup';

import { phoneRegexp } from '@common/react/utils/validationHelpers';
import { FileInterface, FileType } from '@common/typescript/objects/FileInterface';

import { rest } from '@app/components/Api';
import CustomDatePicker from '@app/components/UI/Forms/CustomDatePicker/CustomDatePicker';
import FormikField from '@app/components/UI/Forms/CustomFormikField/CustomFormikField';
import { CustomPhoneControl } from '@app/components/UI/Forms/CustomPhoneControl/CustomPhoneControl';
import CustomSelect from '@app/components/UI/Forms/CustomSelect/CustomSelect';
import { CustomFile } from '@app/components/UI/Forms/CustomFile/CustomFile';
import { validateRequiredString } from '@app/utils/Validators';
import { onFileUpload } from '@app/utils/onFileUpload';
import { FormResponse } from '@app/components/Pages/Area/AreaObjects';
import AttachmentPreview from '@app/components/UI/Attachment/AttachmentPreview';
import CustomInput from '@app/components/UI/CustomInput/CustomInput';

import '@app/scss/components/studentExternshipModal.scss';
import '@app/scss/localCommon/components/attachment.scss';

interface StudentExternshipModalProps {
	modalVisible: boolean;
	setModalVisible: (bool: boolean) => void;
	formSuccess: FormResponse;
	setFormSuccess: (val: FormResponse) => void;
}

const validationSchema = Yup.object({
	firstName: validateRequiredString('First Name is a required field'),
	lastName: validateRequiredString('Last Name is a required field'),
	email: Yup.string().email('Email must be a valid Email').required('Email is a required field'),
	phone: Yup.string().required('Phone Number is a required field').matches(phoneRegexp, 'Invalid Phone Number'),
	vetSchool: validateRequiredString('Veterinarian School is a required field'),
	graduationDate: Yup.number().nullable('Graduation Date is a required field').required('Graduation Date is a required field'),
	source: validateRequiredString('How did you hear about us is a required field'),
	dateRange: validateRequiredString('Date Range is a required field'),
});

const FormSuccess = () => {
	React.useEffect(() => {
		window.scrollTo(0, 0);
		document.querySelector('#react-app')?.scrollTo(0, 0);
	}, []);

	return (
		<div className="externship-inquiry-form__success">
			<h3>Thank you!</h3>
			<h5>We’ve received your information and will be in touch as soon as possible.</h5>
		</div>
	);
};

const StudentExternshipModal: React.FC<StudentExternshipModalProps> = ({
	modalVisible,
	setModalVisible,
	formSuccess,
	setFormSuccess,
}) => {
	const [attachment, setAttachment] = React.useState<Array<FileInterface>>([]);
	const [sending, setSending] = React.useState(false);

	const maxFile = 5000000;
	const imgExtension = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];
	const maxImgCount = 1;
	const sourcesListOptions: Array<{ value: number; label: string }> = [
		{ value: 0, label: 'Conference' },
		{ value: 1, label: 'VBMA talk with Dr. Dani or Dr. Mary' },
		{ value: 2, label: 'CVM Event' },
		{ value: 3, label: 'Previous Lap of Love client' },
		{ value: 4, label: 'Other' },
	];

	return (
		<Modal
			wrapClassName="modal-wrap job-inquiry externship-inquiry"
			open={modalVisible}
			onOk={() => setModalVisible(false)}
			onCancel={() => setModalVisible(false)}
			width={1037}
			centered
		>
			<div className="job-inquiry__content">
				<div className="modal-wrap__container">
					{!formSuccess.success ? (
						<Formik
							onSubmit={(values, formikActions) => {
								setSending(true);
								rest.post('v1/externship', {
									...values,
									graduationDate: values.graduationDate ? new Date(values.graduationDate).toLocaleDateString('en-US', { timeZone: 'UTC' }) : null,
								})
									.then((res) => {
										formikActions.resetForm();
										setAttachment([]);
										setFormSuccess({ success: true, error: '' });
									})
									.finally(() => setSending(false));
							}}
							initialValues={{
								firstName: '',
								lastName: '',
								email: '',
								phone: '',
								vetSchool: '',
								graduationDate: null,
								source: '',
								dateRange: '',
								notes: '',
								filePath: [],
							}}
							validationSchema={validationSchema}
						>
							{(formikBag: FormikProps<any>) => {
								return (
									<Form className="job-inquiry__form externship-inquiry__form" autoComplete="off">
										<h3 className="job-inquiry-title">Externship inquiry</h3>
										<div className="form-group">
											<CustomInput type="text" name="firstName" placeholder="First Name*" formikBag={formikBag} />
											<CustomInput type="text" name="lastName" placeholder="Last Name*" formikBag={formikBag} />
											<CustomInput name="email" placeholder="Email Address*" formikBag={formikBag} trimValue />
											<div className="phone">
												<FormikField
													title="Phone Number*"
													fieldName="phone"
													render={(fieldProps) => (
														<CustomPhoneControl placeholder="Phone Number*" fieldProps={fieldProps} />
													)}
												/>
											</div>
											<CustomInput type="text" name="vetSchool" placeholder="Vet School*" formikBag={formikBag} />
											<CustomDatePicker
												name="graduationDate"
												placeholder="Graduation Date*"
												formikBag={formikBag}
												onChange={(value) => {
													formikBag.setFieldValue('graduationDate', value ? new Date(value.toDate()).setUTCHours(0, 0, 0, 0) : null);
												}}
											/>
										</div>
										<div className="form-group form-group_full-width">
											<CustomSelect
												name="source"
												placeholder="How did you hear about Lap of Love?*"
												onSelect={(value, option) => {
													formikBag.setFieldValue('source', value);
												}}
												onChange={(value, option) => {
													formikBag.setFieldValue('source', value);
												}}
												onSearch={(value, option) => {
													formikBag.setFieldValue('source', value);
												}}
												options={sourcesListOptions}
												showSearch={false}
												onFocus={() => {}}
												transform={false}
												formikBag={formikBag}
											/>
										</div>
										<div className="form-group form-group_textarea">
											<CustomInput
												component="textarea"
												name="dateRange"
												placeholder="What date range are you interested in completing your externship?*"
												formikBag={formikBag}
											/>
											<div
												className={!formikBag.values.dateRange ? 'reminder reminder-visible' : 'reminder reminder-hidden'}
											>
												(Reminder: you must be a 3rd or 4th year vet student when completing the externship)
											</div>

											<CustomInput
												component="textarea"
												name="notes"
												placeholder="Share with us any notes or additional questions you have about the program"
												formikBag={formikBag}
											/>
										</div>
										<div className="form-group_full-width">
											<Field
												name="filePath"
											>
												{(field) => {
													return (
														<div className="attachment modal-wrap">
															<h4 className="attachment-title">Attach a resume</h4>
															<AttachmentPreview
																attachment={attachment}
																setAttachment={setAttachment}
																formikBag={formikBag}
															/>
															<div className="attachment-upload-container">
																<CustomFile
																	update={(data) => onFileUpload(
																		data,
																		field,
																		formikBag,
																		'filePath',
																		maxFile,
																		imgExtension,
																		attachment,
																		maxImgCount,
																		setAttachment,
																		'Resume must be in PDF format',
																	)}
																	type="jobInquiry"
																	onUploadType="jobInquiry"
																	fileType={FileType.File}
																	objectId={-1}
																	noRelation
																	asButton
																	buttonClassName="btn btn-secondary"
																	buttonCaption="Upload a file"
																	multiple
																/>
																<span className="attachment-note">
																	(Maximum file size is 10MB in JPG, PNG or PDF format)
																</span>
															</div>
															{field.form.errors.filePath && field.form.touched.filePath ? (
																<div className="custom-input__error">
																	{' '}
																	{field.form.errors.filePath}
																</div>
															) : null}
														</div>
													);
												}}
											</Field>
										</div>

										<div className="form-group form-group_submit">
											<button type="submit" className="btn btn-md btn-primary" disabled={sending}>
												Submit your application
											</button>
											{Object.values(formikBag.errors).some((el) => el) && formikBag.submitCount > 0 && (
												<div className="submit-validate-error">
													Please fill in all required fields marked with an asterisk
												</div>
											)}
										</div>
										{sending ? (
											<div className="lds-ring">
												<div />
												<div />
												<div />
												<div />
											</div>
										) : null}
									</Form>
								);
							}}
						</Formik>
					) : (
						<FormSuccess />
					)}
				</div>
			</div>
		</Modal>
	);
};

export default StudentExternshipModal;
